import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { listOrders, updateOrderStatus, AdminOrder } from "../../services/adminOrdersService";
import { useAuth } from "../../contexts/AuthContext";

type StatusFilter = "all" | "pending" | "confirmed" | "shipped" | "delivered" | "cancelled";
type SortKey = "newest" | "oldest" | "highest" | "lowest";

const statusOptions = ["pending", "confirmed", "shipped", "delivered", "cancelled"] as const;

const parseJson = <T,>(value: string | null): T | null => {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

const getTenantId = () => {
  const tenant = parseJson<{ id?: string }>(localStorage.getItem("currentTenant"));
  const userData = parseJson<{ tenant_id?: string }>(localStorage.getItem("user_data"));
  const user = parseJson<{ tenant_id?: string }>(localStorage.getItem("user"));
  return tenant?.id || userData?.tenant_id || user?.tenant_id || "";
};

const formatCurrency = (value?: number) =>
  new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 0,
  }).format(Number(value || 0));

const formatDate = (value?: string) => {
  if (!value) return "-";
  const dt = new Date(value);
  return Number.isNaN(dt.getTime()) ? value : dt.toLocaleString();
};

const statusBadgeClass = (status?: string) => {
  const s = (status || "").toLowerCase();
  if (s === "delivered") return "bg-green-100 text-green-700";
  if (s === "cancelled") return "bg-red-100 text-red-700";
  if (s === "shipped") return "bg-blue-100 text-blue-700";
  if (s === "confirmed") return "bg-indigo-100 text-indigo-700";
  return "bg-amber-100 text-amber-700";
};

const paymentBadgeClass = (status?: string) => {
  const s = (status || "").toLowerCase();
  if (s === "paid") return "bg-green-100 text-green-700";
  if (s === "failed") return "bg-red-100 text-red-700";
  if (s === "refunded") return "bg-purple-100 text-purple-700";
  return "bg-gray-100 text-gray-700";
};

const AdminOrders: React.FC = () => {
  const { hasCapability } = useAuth();
  const canAccess = hasCapability("capability:admin_console");
  const tenantId = useMemo(() => getTenantId(), []);

  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [sortKey, setSortKey] = useState<SortKey>("newest");
  const [busyOrderId, setBusyOrderId] = useState<string | null>(null);

  const loadOrders = async (isRefresh = false) => {
    if (!canAccess) return;
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      setError(null);
      const data = await listOrders();
      setOrders(Array.isArray(data) ? data : []);
    } catch (err: any) {
      const detail = err?.response?.data?.detail || "Failed to load orders";
      if (err?.response?.status === 401) {
        setError("Invalid authentication credentials. Please log in again.");
      } else {
        setError(detail);
      }
    } finally {
      if (isRefresh) setRefreshing(false);
      else setLoading(false);
    }
  };

  useEffect(() => {
    if (!canAccess) return;
    loadOrders();
  }, [canAccess]);

  useEffect(() => {
    if (!selectedOrderId && orders.length > 0) {
      setSelectedOrderId(orders[0].id);
      return;
    }
    if (selectedOrderId && !orders.some((o) => o.id === selectedOrderId)) {
      setSelectedOrderId(orders[0]?.id || null);
    }
  }, [orders, selectedOrderId]);

  const filteredOrders = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = orders.filter((order) => {
      const statusOk = statusFilter === "all" ? true : (order.status || "").toLowerCase() === statusFilter;
      const queryOk =
        !q ||
        (order.id || "").toLowerCase().includes(q) ||
        (order.customer_id || "").toLowerCase().includes(q) ||
        (order.product_id || "").toLowerCase().includes(q) ||
        (order.delivery_address || "").toLowerCase().includes(q) ||
        (order.payment_status || "").toLowerCase().includes(q);
      return statusOk && queryOk;
    });

    const getAmount = (o: AdminOrder) => Number(o.total_amount ?? o.total_price ?? 0);
    const getTime = (o: AdminOrder) => new Date(o.created_at || 0).getTime() || 0;

    return [...filtered].sort((a, b) => {
      if (sortKey === "oldest") return getTime(a) - getTime(b);
      if (sortKey === "highest") return getAmount(b) - getAmount(a);
      if (sortKey === "lowest") return getAmount(a) - getAmount(b);
      return getTime(b) - getTime(a);
    });
  }, [orders, search, statusFilter, sortKey]);

  const selectedOrder = useMemo(
    () => filteredOrders.find((o) => o.id === selectedOrderId) || orders.find((o) => o.id === selectedOrderId) || null,
    [filteredOrders, orders, selectedOrderId]
  );

  const metrics = useMemo(() => {
    const totalOrders = orders.length;
    const totalRevenue = orders.reduce(
      (sum, o) => sum + Number(o.total_amount ?? o.total_price ?? 0),
      0
    );
    const delivered = orders.filter((o) => (o.status || "").toLowerCase() === "delivered").length;
    const pending = orders.filter((o) => ["pending", "confirmed"].includes((o.status || "").toLowerCase())).length;
    const shipped = orders.filter((o) => (o.status || "").toLowerCase() === "shipped").length;
    const cancelled = orders.filter((o) => (o.status || "").toLowerCase() === "cancelled").length;
    const fulfillmentRate = totalOrders ? Math.round((delivered / totalOrders) * 100) : 0;
    return { totalOrders, totalRevenue, delivered, pending, shipped, cancelled, fulfillmentRate };
  }, [orders]);

  const handleStatusChange = async (orderId: string, status: string) => {
    setBusyOrderId(orderId);
    try {
      const updated = await updateOrderStatus(orderId, status);
      setOrders((prev) =>
        prev.map((o) =>
          o.id === orderId
            ? {
                ...o,
                ...updated,
                status,
              }
            : o
        )
      );
      toast.success("Order status updated");
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setBusyOrderId(null);
    }
  };

  if (!canAccess) {
    return (
      <div className="p-8 text-center text-red-600 font-semibold text-lg">
        Access denied. Only admins can manage orders.
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 md:p-8 space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Order Management</h1>
          <p className="text-sm text-gray-600 mt-1">
            Tenant order operations dashboard for fulfillment and status tracking.
            {tenantId ? <span className="ml-2 font-mono text-xs">Tenant: {tenantId}</span> : null}
          </p>
        </div>
        <button
          onClick={() => loadOrders(true)}
          disabled={refreshing}
          className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {refreshing ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-xs text-gray-500">Orders</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{metrics.totalOrders}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 lg:col-span-2">
          <div className="text-xs text-gray-500">Revenue (Orders)</div>
          <div className="text-2xl font-bold text-green-700 mt-1">{formatCurrency(metrics.totalRevenue)}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-xs text-gray-500">Pending</div>
          <div className="text-2xl font-bold text-amber-700 mt-1">{metrics.pending}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-xs text-gray-500">Delivered</div>
          <div className="text-2xl font-bold text-green-700 mt-1">{metrics.delivered}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-xs text-gray-500">Fulfillment Rate</div>
          <div className="text-2xl font-bold text-blue-700 mt-1">{metrics.fulfillmentRate}%</div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="grid md:grid-cols-4 gap-3">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search order ID, customer ID, product ID, address..."
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm md:col-span-2"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white"
          >
            <option value="all">All Statuses</option>
            {statusOptions.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value as SortKey)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white"
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="highest">Highest Value</option>
            <option value="lowest">Lowest Value</option>
          </select>
        </div>
      </div>

      {loading && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 text-gray-600">
          Loading orders...
        </div>
      )}

      {!loading && error && (
        <div className="bg-white rounded-xl border border-red-200 p-6">
          <div className="text-red-600 font-semibold mb-2">{error}</div>
          {error.toLowerCase().includes("authentication") && (
            <div className="text-sm text-gray-600">
              Your session token is invalid or expired. Re-login to refresh `auth_token` and `user_data` for this tenant.
            </div>
          )}
        </div>
      )}

      {!loading && !error && filteredOrders.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-8">
          <div className="text-lg font-semibold text-gray-900 mb-2">No orders found</div>
          <div className="text-sm text-gray-600">
            No orders match the current filters, or this tenant has not received any orders yet.
          </div>
        </div>
      )}

      {!loading && !error && filteredOrders.length > 0 && (
        <div className="grid xl:grid-cols-[1.6fr_1fr] gap-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Order</th>
                    <th className="px-4 py-3 text-left font-semibold">Customer</th>
                    <th className="px-4 py-3 text-left font-semibold">Amount</th>
                    <th className="px-4 py-3 text-left font-semibold">Status</th>
                    <th className="px-4 py-3 text-left font-semibold">Payment</th>
                    <th className="px-4 py-3 text-left font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order) => {
                    const isSelected = selectedOrderId === order.id;
                    const rowBusy = busyOrderId === order.id;
                    const currentStatus = (order.status || "pending").toLowerCase();
                    const availableStatuses = currentStatus && !statusOptions.includes(currentStatus as any)
                      ? [currentStatus, ...statusOptions]
                      : [...statusOptions];

                    return (
                      <tr
                        key={order.id}
                        className={`border-t border-gray-100 cursor-pointer ${isSelected ? "bg-green-50/40" : "hover:bg-gray-50"}`}
                        onClick={() => setSelectedOrderId(order.id)}
                      >
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{order.id}</div>
                          <div className="text-xs text-gray-500 mt-1">{formatDate(order.created_at)}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-gray-900">{order.customer_id || "-"}</div>
                          <div className="text-xs text-gray-500 mt-1 truncate max-w-[180px]">
                            {order.product_id || "-"}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-semibold text-gray-900">
                            {formatCurrency(order.total_amount ?? order.total_price)}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">Qty: {order.quantity ?? 0}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${statusBadgeClass(order.status)}`}>
                            {order.status || "pending"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${paymentBadgeClass(order.payment_status)}`}>
                            {order.payment_status || "pending"}
                          </span>
                        </td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <select
                            value={currentStatus}
                            onChange={(e) => handleStatusChange(order.id, e.target.value)}
                            disabled={rowBusy}
                            className="border border-gray-300 rounded-md px-2 py-1 bg-white text-sm disabled:opacity-50"
                          >
                            {availableStatuses.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5 h-fit sticky top-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Detail</h2>
            {!selectedOrder ? (
              <div className="text-sm text-gray-600">Select an order to inspect details.</div>
            ) : (
              <div className="space-y-4 text-sm">
                <div>
                  <div className="text-xs text-gray-500">Order ID</div>
                  <div className="font-mono text-xs text-gray-800 mt-1 break-all">{selectedOrder.id}</div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs text-gray-500">Status</div>
                    <div className="mt-1">
                      <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${statusBadgeClass(selectedOrder.status)}`}>
                        {selectedOrder.status || "pending"}
                      </span>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Payment</div>
                    <div className="mt-1">
                      <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${paymentBadgeClass(selectedOrder.payment_status)}`}>
                        {selectedOrder.payment_status || "pending"}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs text-gray-500">Quantity</div>
                    <div className="font-semibold text-gray-900 mt-1">{selectedOrder.quantity ?? 0}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Order Value</div>
                    <div className="font-semibold text-green-700 mt-1">
                      {formatCurrency(selectedOrder.total_amount ?? selectedOrder.total_price)}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Customer ID</div>
                  <div className="text-gray-900 mt-1 break-all">{selectedOrder.customer_id || "-"}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Product ID</div>
                  <div className="text-gray-900 mt-1 break-all">{selectedOrder.product_id || "-"}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Created</div>
                  <div className="text-gray-900 mt-1">{formatDate(selectedOrder.created_at)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Requested Delivery</div>
                  <div className="text-gray-900 mt-1">{formatDate(selectedOrder.requested_delivery_date)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Delivery Address</div>
                  <div className="text-gray-900 mt-1 whitespace-pre-wrap">
                    {selectedOrder.delivery_address || "-"}
                  </div>
                </div>
                <div className="pt-2 border-t border-gray-100">
                  <div className="text-xs text-gray-500 mb-2">Quick Status Update</div>
                  <select
                    value={(selectedOrder.status || "pending").toLowerCase()}
                    onChange={(e) => handleStatusChange(selectedOrder.id, e.target.value)}
                    disabled={busyOrderId === selectedOrder.id}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white text-sm"
                  >
                    {(selectedOrder.status &&
                    !statusOptions.includes(selectedOrder.status.toLowerCase() as any)
                      ? [selectedOrder.status.toLowerCase(), ...statusOptions]
                      : [...statusOptions]
                    ).map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
